// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import { BackgroundJob } from 'BackgroundJob';
import TokenData from 'TokenData';
import AIChatMessage from '../../models/AIChatMessage';
import { Article, ArticleContent } from '../../models/Article';
import { NOT_FOUND } from '../../services/BaseBackendService';
export const adminBackendServiceArticleMixin = {
  getPublishedArticles(): Promise<Article[]> {
    return this.authApiGet('articles/published', (json) => json.data as Article[]);
  },

  getAllArticles(page: number = 1): Promise<{
    data: Article[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    return this.authApiGetAuthed(
      `articles?page=${page}`,
      'Network error while fetching articles',
      (json) => json,
    );
  },

  archiveArticle(articleId: string): Promise<void> {
    return this.authApiPatchVoid(`articles/${articleId}/archive`);
  },

  unarchiveArticle(articleId: string): Promise<void> {
    return this.authApiPatchVoid(`articles/${articleId}/unarchive`);
  },

  approveArticle(articleId: string): Promise<void> {
    return this.authApiPatchVoid(`articles/${articleId}/approve`);
  },

  getArticleContent(article: Article): Promise<ArticleContent> {
    return this.setupAssetStorageWithRetry(false)
      .then((token: TokenData) =>
        this.getAsset(article.resultLocation!, token.token.token, { url: false }),
      )
      .then((content) => JSON.parse(content as string) as ArticleContent);
  },

  getArticlePdf(article: Article): Promise<string | null> {
    return this.setupAssetStorageWithRetry(false)
      .then((tokenData) =>
        this.getAsset(article.articleLocation, tokenData.token.token, { url: true }),
      )
      .then((url) => url as string)
      .catch((error: any) => (error == NOT_FOUND ? null : Promise.reject(error)));
  },

  getPendingPreviewArticles(
    page: number = 1,
    limit: number = 20,
  ): Promise<{
    data: Article[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    return this.authApiGetAuthed(
      `articles/pendingPreview?page=${page}&limit=${limit}`,
      'Network error while fetching pending preview articles',
      (json) => json,
    );
  },

  promoteArticleToPublished(article: Article): Promise<void> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(
        this.apiURL(`articles/${article.id}/promote`),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        await new Promise<void>((_resolve, reject) => {
          this.handleErrorResponse(response, reject);
        });
      }
    });
  },

  getArticle(id: string): Promise<Article> {
    return this.authApiGetAuthed(
      `articles/${id}`,
      'Network error while fetching article',
      (data) => data.data as Article,
    );
  },

  refineArticleContent(
    articleId: string,
    message: string | null,
    chatHistory: AIChatMessage[] = [],
    isRegeneration: boolean = false,
    contentModel?: string,
  ): Promise<{ jobId: string }> {
    return this.authApiMutateAuthed(
      `articles/${articleId}/refine`,
      'POST',
      { message, chatHistory, contentModel, isRegeneration },
      (data) => data.data,
    );
  },

  getUserBackgroundJobs(): Promise<BackgroundJob[]> {
    return this.authApiGetAuthed(
      'jobs',
      'Network error while fetching background jobs',
      (json) => json.data as BackgroundJob[],
    );
  },

  getAllJobs(): Promise<BackgroundJob[]> {
    return this.authApiGetAuthed(
      'jobs/all',
      'Network error while fetching all jobs',
      (json) => json.data as BackgroundJob[],
    );
  },

  dismissBackgroundJob(jobId: string): Promise<void> {
    return this.authApiPatchVoid(`jobs/${jobId}/dismiss`);
  },

  getJobById(jobId: string): Promise<BackgroundJob> {
    return this.authApiGetAuthed(
      `jobs/${jobId}`,
      'Network error while fetching job',
      (json) => json.data as BackgroundJob,
    );
  },

  getArticleJobs(articleId: string): Promise<BackgroundJob[]> {
    return this.authApiGetAuthed(
      `articles/${articleId}/jobs`,
      'Network error while fetching article jobs',
      (json) => json.data as BackgroundJob[],
    );
  },
} satisfies ThisType<BaseBackendService>;
